import React, { Component } from 'react';
import PropTypes from 'prop-types';
import css from './Page.module.css';
import classNames from 'classnames';
import { Helmet } from 'react-helmet-async';
import { withRouter } from 'react-router-dom';
import config from '../../config/config';
import { t } from '../../util/messages';
import { canonicalRoutePath } from '../../util/routes';
import defaultShareImage from '../../assets/og-image.png';
import { metaTagProps } from '../../util/seo';

const preventDefault = e => e.preventDefault();

/**
 * Share image used by every page that does not supply its own.
 *
 * Open Graph was previously emitted for the landing page alone, so every
 * other URL shared into Slack, LinkedIn or WhatsApp rendered as a bare link.
 * The cause was two defaults below: `contentType: null` made
 * openGraphMetaProps bail out entirely (it requires one), and no page but
 * the landing page passed an image.
 *
 * The dimensions are the file's real ones (1200x630, the size every platform
 * documents). A wrong og:image:width makes a scraper crop or skip the image,
 * so change these two numbers whenever og-image.png is re-exported.
 */
const SHARE_IMAGE_WIDTH = 1200;
const SHARE_IMAGE_HEIGHT = 630;

const defaultShareImages = () => [
  {
    url: `${config.canonicalRootURL}${defaultShareImage}`,
    width: SHARE_IMAGE_WIDTH,
    height: SHARE_IMAGE_HEIGHT,
  },
];

class PageComponent extends Component {
  constructor(props) {
    super(props);
    this.scrollPosition = 0;
    this.contentDiv = null;
    this.scrollingDisabledChanged = this.scrollingDisabledChanged.bind(this);
  }

  componentDidMount() {
    document.addEventListener('dragover', preventDefault);
    document.addEventListener('drop', preventDefault);

    /*
     * This used to delete `#page-schema` on mount, to drop a server-rendered
     * copy before the client wrote its own. There is no server renderer, so
     * the only element with that id was the one Helmet had just written —
     * every page shipped with its structured data deleted a frame after it
     * appeared, leaving only the static Organization block in index.html.
     * Nothing to clean up here; Helmet owns that tag.
     */
  }

  componentWillUnmount() {
    document.removeEventListener('dragover', preventDefault);
    document.removeEventListener('drop', preventDefault);
  }

  scrollingDisabledChanged(currentScrollingDisabled) {
    if (currentScrollingDisabled && currentScrollingDisabled !== this.scrollingDisabled) {
      this.scrollPosition =
        window.pageYOffset || document.documentElement.scrollTop;
    }
    this.scrollingDisabled = currentScrollingDisabled;
  }

  render() {
    const {
      className,
      rootClassName,
      children,
      location,
      scrollingDisabled,
      referrer,
      author,
      contentType,
      description,
      facebookImages,
      published,
      schema,
      tags,
      title,
      twitterHandle,
      twitterImages,
      updated,
    } = this.props;

    const classes = classNames(rootClassName || css.root, className, {
      [css.scrollingDisabled]: scrollingDisabled,
    });

    if (typeof window !== 'undefined') {
      this.scrollingDisabledChanged(scrollingDisabled);
    }

    const canonicalRootURL = config.canonicalRootURL;
    const shouldReturnPathOnly = referrer && referrer !== 'unsafe-url';
    const canonicalPath = canonicalRoutePath(location, shouldReturnPathOnly);
    const canonicalUrl = `${canonicalRootURL}${canonicalPath}`;

    const siteTitle = config.siteTitle;
    const schemaTitle = t('Page.schemaTitle', { siteTitle });
    const schemaDescription = t('Page.schemaDescription');
    const metaTitle = title || schemaTitle;
    const metaDescription = description || schemaDescription;

    const metaToHead = metaTagProps({
      author,
      contentType,
      description: metaDescription,
      facebookImages: facebookImages?.length ? facebookImages : defaultShareImages(),
      twitterImages: twitterImages?.length ? twitterImages : defaultShareImages(),
      published,
      tags,
      title: metaTitle,
      twitterHandle,
      updated,
      url: canonicalUrl,
    });

    const metaTags = metaToHead.map((metaProps, i) => (
      // eslint-disable-next-line react/no-array-index-key
      <meta key={i} {...metaProps} />
    ));

    const { organization } = config;
    const sameOrganizationAs = [
      config.siteLinkdinPage,
      config.siteFacebookPage,
      config.siteInstagramPage,
    ].filter(Boolean);

    /*
     * One Organization node, referenced by @id everywhere else, so search
     * engines resolve a single entity rather than several competing ones.
     * `ProfessionalService` is the narrower type and is what agencies are
     * expected to use; it still inherits everything Organization carries.
     */
    const schemaFromProps = Array.isArray(schema) ? schema : [schema];
    const schemaArrayJSONString = JSON.stringify([
      ...schemaFromProps,
      {
        '@context': 'https://schema.org',
        '@type': ['Organization', 'ProfessionalService'],
        '@id': `${canonicalRootURL}/#organization`,
        url: canonicalRootURL,
        name: siteTitle,
        legalName: organization.legalName,
        description: schemaDescription,
        sameAs: sameOrganizationAs,
        logo: {
          '@type': 'ImageObject',
          url: `${canonicalRootURL}${organization.logoPath}`,
          width: 192,
          height: 192,
        },
        foundingDate: organization.foundingDate,
        knowsAbout: organization.knowsAbout,
        address: organization.address,
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'sales',
          email: organization.email,
          telephone: organization.telephone,
          availableLanguage: ['English'],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': `${canonicalRootURL}/#website`,
        url: canonicalRootURL,
        description: schemaDescription,
        name: schemaTitle,
        inLanguage: 'en',
        publisher: { '@id': `${canonicalRootURL}/#organization` },
      },
    ]);

    const scrollPositionStyles = scrollingDisabled
      ? { marginTop: `${-1 * this.scrollPosition}px` }
      : {};

    if (
      typeof window !== 'undefined' &&
      !scrollingDisabled &&
      this.contentDiv?.style?.marginTop
    ) {
      window.requestAnimationFrame(() => {
        const hash = window.location.hash;
        if (hash) {
          const el = document.getElementById(hash.replace('#', ''));
          if (el) {
            el.scrollIntoView({ behavior: 'smooth' });
            return;
          }
        }
        window.scrollTo(0, this.scrollPosition);
      });
    }

    return (
      <div className={classes}>
        <Helmet htmlAttributes={{ lang: 'en' }}>
          <title>{metaTitle}</title>
          {referrer && <meta name="referrer" content={referrer} />}
          <link rel="canonical" href={canonicalUrl} />
          <meta httpEquiv="Content-Type" content="text/html; charset=UTF-8" />
          <meta httpEquiv="Content-Language" content="en" />
          {/* No viewport here: public/index.html declares it, and it must
              apply before this component mounts. Emitting it from both put
              two viewport tags in every prerendered page. */}
          {/* Lets Google show large image previews rather than thumbnails. */}
          <meta
            name="robots"
            content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
          />
          {metaTags}
          <script id="page-schema" type="application/ld+json">
            {schemaArrayJSONString.replace(/</g, '\\u003c')}
          </script>
        </Helmet>
        <div
          className={css.content}
          style={scrollPositionStyles}
          ref={c => { this.contentDiv = c; }}
        >
          {children}
        </div>
      </div>
    );
  }
}

PageComponent.defaultProps = {
  className: '',
  rootClassName: '',
  children: null,
  location: {},
  scrollingDisabled: false,
  referrer: null,
  author: null,
  /* Not null: openGraphMetaProps returns [] without it, which silently
     dropped every og: tag on every page that did not set it. */
  contentType: 'website',
  description: null,
  facebookImages: [],
  published: null,
  schema: [],
  tags: [],
  title: '',
  twitterHandle: null,
  twitterImages: [],
  updated: null,
};

PageComponent.propTypes = {
  className: PropTypes.string,
  rootClassName: PropTypes.string,
  children: PropTypes.node,
  location: PropTypes.object,
  scrollingDisabled: PropTypes.bool,
  referrer: PropTypes.string,
  author: PropTypes.string,
  contentType: PropTypes.string,
  description: PropTypes.string,
  facebookImages: PropTypes.array,
  published: PropTypes.string,
  schema: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  tags: PropTypes.array,
  title: PropTypes.string,
  twitterHandle: PropTypes.string,
  twitterImages: PropTypes.array,
  updated: PropTypes.string,
};

const Page = withRouter(PageComponent);
Page.displayName = 'Page';

export default Page;
